import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../utils/api';

/**
 * Hook for checking new messages and showing notifications
 * Checks every minute for new messages
 */
export const useMessageNotifications = (organizationId) => {
    const navigate = useNavigate();
    const [unreadCount, setUnreadCount] = useState(0);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [hasCheckedInitial, setHasCheckedInitial] = useState(false);
    const previousCountRef = useRef(0);
    const hasShownInitialNotificationRef = useRef(false);
    const audioRef = useRef(null);
    const lastSoundPlayTimeRef = useRef(0);
    const notificationOpenRef = useRef(false);
    const audioContextRef = useRef(null);
    const audioInitializedRef = useRef(false);

    // Initialize audio context on first user interaction
    useEffect(() => {
        const initAudio = () => {
            if (audioInitializedRef.current && audioContextRef.current) return;
            
            try {
                // Create AudioContext once
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                audioInitializedRef.current = true;
                console.log('🔊 Audio context initialized');
                
                // Try to resume if suspended (non-blocking)
                if (audioContextRef.current.state === 'suspended') {
                    audioContextRef.current.resume().then(() => {
                        console.log('🔊 Audio context resumed on init');
                    }).catch(err => {
                        console.warn('Failed to resume audio context on init:', err);
                    });
                }
            } catch (error) {
                console.error('Failed to initialize audio context:', error);
            }
        };

        // Try to initialize on mount (may fail without user interaction)
        initAudio();

        // Initialize/resume on first user interaction (non-blocking)
        const handleUserInteraction = () => {
            if (!audioInitializedRef.current || !audioContextRef.current) {
                initAudio();
            }
            
            // Resume if suspended (non-blocking)
            if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume().then(() => {
                    console.log('🔊 Audio context resumed on user interaction');
                }).catch(err => {
                    console.warn('Failed to resume audio context:', err);
                });
            }
        };

        // Add listeners for user interaction
        document.addEventListener('click', handleUserInteraction, { once: true, passive: true });
        document.addEventListener('touchstart', handleUserInteraction, { once: true, passive: true });
        document.addEventListener('keydown', handleUserInteraction, { once: true });

        return () => {
            document.removeEventListener('click', handleUserInteraction);
            document.removeEventListener('touchstart', handleUserInteraction);
            document.removeEventListener('keydown', handleUserInteraction);
        };
    }, []);

    // Check for unread messages
    const checkUnreadMessages = async () => {
        if (!organizationId) return;

        try {
            const response = await apiRequest(`/organizations/${organizationId}/contact/unread-count`);
            // Backend returns: { success: true, data: { unread_count: ... } }
            const count = response.data?.unread_count || response.unread_count || 0;
            const previousCount = previousCountRef.current;

            console.log('📬 Message check:', { count, previousCount, organizationId, notificationOpen: notificationOpenRef.current });

            // Update count
            setUnreadCount(count);
            
            // If count changed (increased or decreased but still > 0)
            if (count !== previousCount && hasCheckedInitial) {
                // If count increased (new messages)
                if (count > previousCount) {
                    const newMessages = count - previousCount;
                    console.log('🔔 New messages detected:', newMessages);
                    setNotificationMessage(
                        count === 1 
                            ? '1 yeni mesaj var' 
                            : `${count} yeni mesaj var`
                    );
                    setShowNotification(true);
                    notificationOpenRef.current = true;
                    
                    // Play notification sound (non-blocking)
                    playNotificationSound();
                }
                // If count changed but notification is already open, update the message
                else if (showNotification && count > 0) {
                    console.log('🔄 Updating notification count:', count);
                    setNotificationMessage(
                        count === 1 
                            ? '1 yeni mesaj var' 
                            : `${count} yeni mesaj var`
                    );
                    
                    // Always play sound when count updates (if notification is open)
                    console.log('🔔 Playing sound for updated notification');
                    playNotificationSound();
                }
                // If count is 0, close notification
                else if (count === 0) {
                    setShowNotification(false);
                    notificationOpenRef.current = false;
                }
            }
            
            // If notification is open and there are unread messages, check if we should play sound again
            if (showNotification && count > 0 && hasCheckedInitial) {
                const now = Date.now();
                // Play sound every minute if notification is still open
                if (now - lastSoundPlayTimeRef.current >= 60000) {
                    console.log('🔔 Repeating notification sound (1 minute interval)');
                    playNotificationSound();
                }
            }
            
            // Update previous count after checking
            previousCountRef.current = count;
        } catch (error) {
            console.error('❌ Check unread messages error:', error);
        }
    };

    // Play notification sound
    const playNotificationSound = () => {
        playBeepSound();
    };

    // Fallback beep sound using Web Audio API (non-blocking)
    const playBeepSound = () => {
        // Update last sound play time immediately
        lastSoundPlayTimeRef.current = Date.now();
        
        // Play sound asynchronously without blocking
        (async () => {
            try {
                // Get or create audio context
                let audioContext = audioContextRef.current;
                
                if (!audioContext) {
                    try {
                        audioContext = new (window.AudioContext || window.webkitAudioContext)();
                        audioContextRef.current = audioContext;
                        audioInitializedRef.current = true;
                    } catch (e) {
                        console.error('Failed to create audio context:', e);
                        return;
                    }
                }
                
                // Resume if suspended (non-blocking)
                if (audioContext.state === 'suspended') {
                    try {
                        // Don't await - let it resume in background
                        audioContext.resume().then(() => {
                            console.log('🔊 Audio context resumed');
                            // Try to play sound after resume
                            playSoundNow(audioContext);
                        }).catch(err => {
                            console.warn('Failed to resume audio context:', err);
                        });
                        return; // Exit early, will play after resume
                    } catch (err) {
                        console.warn('Failed to resume audio context:', err);
                        return;
                    }
                }
                
                // Play sound immediately if context is running
                playSoundNow(audioContext);
            } catch (error) {
                console.error('Beep sound error:', error);
            }
        })();
    };
    
    // Helper function to actually play the sound
    const playSoundNow = (audioContext) => {
        try {
            // Create oscillator and gain node
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            // Configure sound
            oscillator.frequency.value = 800;
            oscillator.type = 'sine';
            
            // Fade in/out
            gainNode.gain.setValueAtTime(0, audioContext.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.05);
            gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.3);
            gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.5);
            
            // Play sound
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.5);
            
            // Clean up
            oscillator.onended = () => {
                oscillator.disconnect();
                gainNode.disconnect();
            };
            
            console.log('🔊 Notification sound played');
        } catch (error) {
            console.error('Play sound now error:', error);
        }
    };

    // Initial check on mount
    useEffect(() => {
        if (organizationId && !hasCheckedInitial) {
            checkUnreadMessages().then(() => {
                setHasCheckedInitial(true);
            });
        }
    }, [organizationId, hasCheckedInitial]);

    // Show notification on first load if there are unread messages
    useEffect(() => {
        if (hasCheckedInitial && unreadCount > 0 && !hasShownInitialNotificationRef.current) {
            console.log('🔔 Showing initial notification for', unreadCount, 'messages');
            hasShownInitialNotificationRef.current = true;
            setNotificationMessage(
                unreadCount === 1 
                    ? '1 yeni mesaj var' 
                    : `${unreadCount} yeni mesaj var`
            );
            setShowNotification(true);
            notificationOpenRef.current = true;
            playNotificationSound();
        }
    }, [hasCheckedInitial, unreadCount]);

    // Check every minute (60000ms)
    useEffect(() => {
        if (!organizationId || !hasCheckedInitial) return;

        // Check immediately when interval is set up (after initial check)
        checkUnreadMessages();

        const interval = setInterval(() => {
            checkUnreadMessages();
        }, 60000); // 1 minute

        return () => clearInterval(interval);
    }, [organizationId, hasCheckedInitial]);

    // Handle "Go to Messages" button click
    const handleGoToMessages = () => {
        setShowNotification(false);
        notificationOpenRef.current = false;
        navigate(`/admin/organizations/${organizationId}/messages`);
    };

    // Close notification
    const handleCloseNotification = () => {
        setShowNotification(false);
        notificationOpenRef.current = false;
    };
    
    // Update notificationOpenRef when showNotification changes
    useEffect(() => {
        notificationOpenRef.current = showNotification;
    }, [showNotification]);

    return {
        unreadCount,
        showNotification,
        notificationMessage,
        handleGoToMessages,
        handleCloseNotification
    };
};

